import { useState, useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { T } from '../lib/tokens'
import { useChatSessions } from '../lib/hooks'
import { askLake, modelLabel as apiModelLabel } from '../lib/api'
import { supabase } from '../lib/supabase'
import { Sidebar } from '../components/Sidebar'
import { Topbar } from '../components/Topbar'
import { GeminiDrawer, useGeminiDrawer } from '../components/GeminiDrawer'
import { LoadingSpinner, MarkdownLite } from '../components/ui'

interface Source { item_id: string; title: string }
interface Row {
  id: string
  role: 'user' | 'assistant'
  content: string
  sources?: Source[] | null
  searched?: boolean
  model?: string | null
  created_at: string
}
interface IndexEntry { count: number; last: string; searched: boolean; text: string }
type Period = 'all' | 'today' | 'week' | 'month'

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString('ja-JP', { year: '2-digit', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
}

function modelLabel(model?: string | null): string {
  if (!model) return ''
  return apiModelLabel(model)
}

export function ChatsPage() {
  const qc = useQueryClient()
  const { open: geminiOpen, setOpen: setGeminiOpen } = useGeminiDrawer()
  const { data: sessions = [], isLoading } = useChatSessions()

  const [index, setIndex]       = useState<Record<string, IndexEntry>>({})
  const [totalMsgs, setTotalMsgs] = useState(0)
  const [selected, setSelected] = useState<string | null>(null)
  const [messages, setMessages] = useState<Row[]>([])
  const [loadingMsg, setLoadingMsg] = useState(false)
  const [input, setInput]       = useState('')
  const [sending, setSending]   = useState(false)
  const [useContext, setUseContext] = useState(true)
  const [useMemos, setUseMemos]     = useState(true)

  // フィルタ
  const [search, setSearch]           = useState('')
  const [period, setPeriod]           = useState<Period>('all')
  const [searchedOnly, setSearchedOnly] = useState(false)

  // タイトル編集
  const [editingTitle, setEditingTitle] = useState(false)
  const [titleDraft, setTitleDraft]     = useState('')

  const bottomRef = useRef<HTMLDivElement>(null)
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [messages])

  async function loadIndex() {
    const { data } = await supabase
      .from('ml_chats')
      .select('session_id,content,role,searched,created_at')
      .order('created_at', { ascending: false })
      .limit(3000)
    const idx: Record<string, IndexEntry> = {}
    let total = 0
    for (const r of (data ?? []) as any[]) {
      total++
      if (!idx[r.session_id]) idx[r.session_id] = { count: 0, last: '', searched: false, text: '' }
      const e = idx[r.session_id]
      e.count++
      if (!e.last) e.last = r.content // 降順なので最初に出たものが最新
      if (r.searched) e.searched = true
      e.text += ' ' + (r.content ?? '')
    }
    setIndex(idx)
    setTotalMsgs(total)
  }
  useEffect(() => { loadIndex() }, [])

  async function selectSession(id: string) {
    setSelected(id)
    setEditingTitle(false)
    setLoadingMsg(true)
    const { data } = await supabase
      .from('ml_chats').select('*').eq('session_id', id).order('created_at', { ascending: true })
    setMessages((data ?? []) as Row[])
    setLoadingMsg(false)
  }

  async function send() {
    const q = input.trim()
    if (!q || !selected || sending) return
    const sid = selected
    setMessages(prev => [...prev, { id: 'tmp-u-' + Date.now(), role: 'user', content: q, created_at: new Date().toISOString() }])
    setInput('')
    setSending(true)
    try {
      await supabase.from('ml_chats').insert({ session_id: sid, role: 'user', content: q })
      const history = useContext ? messages.slice(-12).map(m => ({ role: m.role, content: m.content })) : []
      const data = await askLake(q, { history, useMemos })
      const answer  = data.answer ?? JSON.stringify(data)
      const sources = data.sources ?? []
      const model   = data.model ?? null
      setMessages(prev => [...prev, { id: 'tmp-a-' + Date.now(), role: 'assistant', content: answer, sources, model, created_at: new Date().toISOString() }])
      await supabase.from('ml_chats').insert({ session_id: sid, role: 'assistant', content: answer, sources, model })
      await supabase.from('ml_chat_sessions').update({ updated_at: new Date().toISOString() }).eq('id', sid)
      qc.invalidateQueries({ queryKey: ['chat-sessions'] })
      loadIndex()
    } catch {
      setMessages(prev => [...prev, { id: 'err-' + Date.now(), role: 'assistant', content: '⚠️ 通信エラーが発生しました。', created_at: new Date().toISOString() }])
    } finally {
      setSending(false)
    }
  }

  async function saveTitle() {
    const t = titleDraft.trim()
    setEditingTitle(false)
    if (!selected || !t) return
    await supabase.from('ml_chat_sessions').update({ title: t }).eq('id', selected)
    qc.invalidateQueries({ queryKey: ['chat-sessions'] })
  }

  async function deleteSession() {
    if (!selected || !confirm('このスレッドを削除しますか？')) return
    await supabase.from('ml_chat_sessions').delete().eq('id', selected)
    setSelected(null)
    setMessages([])
    qc.invalidateQueries({ queryKey: ['chat-sessions'] })
    loadIndex()
  }

  function exportMarkdown() {
    if (!current) return
    const title = current.title || '無題のスレッド'
    const now = new Date()
    const lines: string[] = [
      `# ${title}`, '',
      `作成: ${fmtDate(current.created_at)} / 更新: ${fmtDate(current.updated_at)} / 出力: ${fmtDate(now.toISOString())}`, '',
      '---', '',
    ]
    for (const m of messages) {
      lines.push(`## ${m.role === 'user' ? 'あなた' : 'Gemini'}（${fmtDate(m.created_at)}）`, '', m.content, '')
      if (m.sources && m.sources.length) {
        lines.push('**参照:**', '')
        for (const s of m.sources) {
          const t = (s.title || s.item_id).replace(/\s+/g, ' ').trim().slice(0, 60)
          lines.push(`- [${t}](/items/${s.item_id})`)
        }
        lines.push('')
      }
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/markdown' })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement('a')
    a.href = url
    const fnDate = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`
    const safe   = title.replace(/[\\/:*?"<>|\n\r]+/g, '_').slice(0, 50).trim() || 'chat'
    a.download = `${safe}_${fnDate}.md`
    a.click()
    URL.revokeObjectURL(url)
  }

  function copy(text: string) { navigator.clipboard?.writeText(text) }

  const current = sessions.find(s => s.id === selected)

  // フィルタ適用
  const now = Date.now()
  const span = period === 'today' ? 86400000 : period === 'week' ? 7 * 86400000 : period === 'month' ? 30 * 86400000 : Infinity
  const q = search.trim().toLowerCase()
  const filtered = sessions.filter(s => {
    if (span !== Infinity && now - new Date(s.updated_at).getTime() > span) return false
    if (searchedOnly && !index[s.id]?.searched) return false
    if (q) {
      const inTitle = (s.title ?? '').toLowerCase().includes(q)
      const inText  = (index[s.id]?.text ?? '').toLowerCase().includes(q)
      if (!inTitle && !inText) return false
    }
    return true
  })

  return (
    <div style={{ display:'flex',height:'100dvh',background:T.bg,color:T.text,fontFamily:T.font,overflow:'hidden' }}>
      <Sidebar />
      <div style={{ flex:1,display:'flex',flexDirection:'column',overflow:'hidden' }}>
        <Topbar subtitle="チャット履歴" onGemini={() => setGeminiOpen(true)} />

        <div style={{ flex:1,display:'flex',overflow:'hidden' }}>

          {/* 左: スレッド一覧 + 検索/フィルタ */}
          <div style={{ width:320,flexShrink:0,borderRight:`1px solid ${T.border}`,display:'flex',flexDirection:'column' }}>
            <div style={{ padding:'12px 12px 8px',borderBottom:`1px solid ${T.border}`,display:'flex',flexDirection:'column',gap:8 }}>
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="🔍 タイトル・本文を検索…"
                style={{ background:T.bgRaised,border:`1px solid ${T.borderHi}`,borderRadius:6,padding:'7px 10px',color:T.text,fontSize:12,fontFamily:T.font,outline:'none' }}
              />
              <div style={{ display:'flex',gap:4,flexWrap:'wrap' }}>
                {(['all','today','week','month'] as Period[]).map(p => (
                  <button key={p} onClick={() => setPeriod(p)} style={pill(period===p)}>
                    {{ all:'全て', today:'今日', week:'今週', month:'今月' }[p]}
                  </button>
                ))}
                <button onClick={() => setSearchedOnly(v=>!v)} style={pill(searchedOnly)}>🔍検索あり</button>
              </div>
              <div style={{ fontSize:10,fontFamily:T.fontMono,color:T.textMute }}>
                {sessions.length} スレッド · {totalMsgs} メッセージ
              </div>
            </div>

            <div style={{ flex:1,overflowY:'auto',padding:10,display:'flex',flexDirection:'column',gap:6 }}>
              {isLoading && <LoadingSpinner />}
              {!isLoading && filtered.length === 0 && (
                <div style={{ fontSize:12,color:T.textMute,padding:'12px 4px',lineHeight:1.7 }}>
                  {sessions.length === 0
                    ? '会話はまだありません。⌘J の「Ask the Lake」で質問すると残ります。'
                    : '条件に合うスレッドがありません。'}
                </div>
              )}
              {filtered.map(s => (
                <button key={s.id} onClick={() => selectSession(s.id)} style={{
                  textAlign:'left',
                  background: s.id === selected ? T.accentSoft : T.bgRaised,
                  border:`1px solid ${s.id === selected ? T.accentBorder : T.border}`,
                  borderRadius:6,padding:'10px 12px',cursor:'pointer',fontFamily:T.font,
                  display:'flex',flexDirection:'column',gap:3,
                }}>
                  <div style={{ display:'flex',alignItems:'center',gap:6 }}>
                    <span style={{ flex:1,fontSize:13,color:T.text,fontWeight:500,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>
                      {s.title || '無題のスレッド'}
                    </span>
                    {index[s.id]?.searched && <span style={{ fontSize:10 }}>🔍</span>}
                  </div>
                  {index[s.id]?.last && (
                    <div style={{ fontSize:11,color:T.textDim,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>
                      {index[s.id].last}
                    </div>
                  )}
                  <div style={{ fontSize:10,color:T.textMute,fontFamily:T.fontMono }}>
                    {fmtDate(s.updated_at)} · {index[s.id]?.count ?? 0}件
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* 右: 会話表示 + 続き入力 */}
          <div style={{ flex:1,display:'flex',flexDirection:'column',overflow:'hidden' }}>
            {!selected ? (
              <div style={{ flex:1,display:'flex',alignItems:'center',justifyContent:'center',color:T.textMute,fontSize:13 }}>
                左のスレッドを選ぶと会話が表示されます。
              </div>
            ) : (
              <>
                {/* ヘッダー（タイトル編集・操作） */}
                <div style={{ padding:'16px 24px 12px',borderBottom:`1px solid ${T.border}` }}>
                  <div style={{ display:'flex',alignItems:'center',gap:8,maxWidth:780 }}>
                    {editingTitle ? (
                      <input
                        value={titleDraft} autoFocus
                        onChange={e => setTitleDraft(e.target.value)}
                        onKeyDown={e => { if (e.key==='Enter') saveTitle(); if (e.key==='Escape') setEditingTitle(false) }}
                        onBlur={saveTitle}
                        style={{ flex:1,fontSize:18,fontWeight:700,color:T.text,background:T.bgRaised,border:`1px solid ${T.borderHi}`,borderRadius:6,padding:'4px 8px',fontFamily:'inherit',outline:'none' }}
                      />
                    ) : (
                      <h1 style={{ flex:1,fontSize:18,fontWeight:700,color:T.text,margin:0,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>
                        {current?.title || '無題のスレッド'}
                      </h1>
                    )}
                    <IconBtn label="✎ 名前" onClick={() => { setTitleDraft(current?.title ?? ''); setEditingTitle(true) }} />
                    <IconBtn label="⬇ 書き出し" onClick={exportMarkdown} />
                    <IconBtn label="🗑 削除" danger onClick={deleteSession} />
                  </div>
                  <div style={{ fontSize:11,fontFamily:T.fontMono,color:T.textMute,marginTop:6 }}>
                    作成 {current ? fmtDate(current.created_at) : '—'} · 更新 {current ? fmtDate(current.updated_at) : '—'} · {messages.length}件
                  </div>
                </div>

                {/* メッセージ */}
                <div style={{ flex:1,overflowY:'auto',padding:'20px 24px' }}>
                  {loadingMsg && <LoadingSpinner />}
                  <div style={{ display:'flex',flexDirection:'column',gap:16,maxWidth:780 }}>
                    {messages.map(m => (
                      <div key={m.id} style={{ display:'flex',flexDirection:'column',alignItems:m.role==='user'?'flex-end':'flex-start',gap:4 }}>
                        <div style={{
                          maxWidth:'90%',
                          background:m.role==='user'?T.accentSoft:T.bgRaised,
                          border:`1px solid ${m.role==='user'?T.accentBorder:T.border}`,
                          borderRadius:8,padding:'10px 13px',fontSize:13,color:T.text,lineHeight:1.7,
                          whiteSpace:m.role==='user'?'pre-wrap':'normal',wordBreak:'break-word',
                        }}>
                          {m.role==='assistant' ? <MarkdownLite text={m.content} /> : m.content}
                        </div>
                        {m.sources && m.sources.length > 0 && (
                          <div style={{ display:'flex',flexDirection:'column',gap:3,paddingLeft:2 }}>
                            {m.sources.map(src => (
                              <a key={src.item_id} href={`/items/${src.item_id}`} style={{ fontSize:11,color:T.textDim,textDecoration:'none' }}>
                                ↗ {src.title || src.item_id}
                              </a>
                            ))}
                          </div>
                        )}
                        <div style={{ display:'flex',gap:8,alignItems:'center',fontSize:10,fontFamily:T.fontMono,color:T.textMute }}>
                          <span>{fmtDate(m.created_at)}</span>
                          {m.role==='assistant' && m.model && <span>{modelLabel(m.model)}</span>}
                          {m.searched && <span>🔍 ウェブ検索あり</span>}
                          {m.role==='assistant' && (
                            <button onClick={() => copy(m.content)} title="回答をコピー" style={{ background:'transparent',border:'none',color:T.textMute,cursor:'pointer',fontFamily:T.fontMono,fontSize:10,padding:0 }}>📋 コピー</button>
                          )}
                        </div>
                      </div>
                    ))}
                    <div ref={bottomRef} />
                  </div>
                </div>

                {/* 続きを書く */}
                <div style={{ borderTop:`1px solid ${T.border}`,padding:16 }}>
                  <div style={{ display:'flex',gap:6,marginBottom:8,maxWidth:780 }}>
                    <button onClick={() => setUseContext(v=>!v)} title="前の会話を踏まえて続ける" style={pill(useContext)}>
                      {useContext ? '🧵 文脈 ON' : '文脈 OFF'}
                    </button>
                    <button onClick={() => setUseMemos(v=>!v)} title="保存メモ全体を参照する" style={pill(useMemos)}>
                      {useMemos ? '📚 メモ参照 ON' : 'メモ参照 OFF'}
                    </button>
                  </div>
                  <div style={{ display:'flex',alignItems:'center',gap:8,maxWidth:780,background:T.bgRaised,border:`1px solid ${T.borderHi}`,borderRadius:8,padding:'8px 12px' }}>
                    <input
                      value={input}
                      onChange={e => setInput(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && !e.shiftKey && send()}
                      placeholder="このスレッドの続きを書く…"
                      disabled={sending}
                      style={{ flex:1,background:'transparent',border:'none',outline:'none',color:T.text,fontSize:13,fontFamily:T.font }}
                    />
                    <button onClick={send} disabled={!input.trim()||sending} style={{
                      background:input.trim()?T.accent:T.bgHover,border:'none',borderRadius:5,
                      color:input.trim()?'#0a0b0e':T.textMute,padding:'6px 12px',fontSize:12,fontWeight:600,
                      cursor:input.trim()?'pointer':'default',fontFamily:T.fontMono,
                    }}>{sending ? '送信中…' : '↵ 送信'}</button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <GeminiDrawer open={geminiOpen} onClose={() => setGeminiOpen(false)} />
    </div>
  )
}

function pill(active: boolean): React.CSSProperties {
  return {
    background: active ? T.bgActive : 'transparent',
    border: `1px solid ${active ? T.borderHi : T.border}`,
    color: active ? T.text : T.textDim,
    borderRadius: 5, padding: '3px 9px', fontSize: 11, cursor: 'pointer', fontFamily: T.fontMono,
  }
}

function IconBtn({ label, onClick, danger }: { label: string; onClick: () => void; danger?: boolean }) {
  return (
    <button onClick={onClick} style={{
      flexShrink: 0, background: 'transparent',
      border: `1px solid ${danger ? T.warn + '60' : T.border}`,
      color: danger ? T.warn : T.textDim,
      borderRadius: 5, padding: '5px 10px', fontSize: 11, cursor: 'pointer', fontFamily: T.fontMono,
    }}>{label}</button>
  )
}
